import { useEffect, useRef } from 'react';

import type { EventPayloadMap, GlobalEvent } from './globalEvents';

type Handler<Payload> = (event: Payload) => unknown;
type Trigger<Payload> = (...data: OptionalArgTuple<Payload>) => void;
type OptionalArgTuple<T> = T extends undefined | void ? [] : [T];
type GlobalEventListener<T> = (event: CustomEvent<T>) => void;
/**
 * A custom React hook for managing global events across the application.
 *
 * This hook allows components to listen for and trigger global events, facilitating
 * communication between different parts of the application in a decoupled manner.
 *
 * @template EventName - The name of the global event (must be a key of EventPayloadMap).
 * @template T - The type of the event payload.
 *
 * @param {EventName} eventName - The name of the global event to listen for or trigger.
 * @param {Handler<EventPayloadMap<T>[EventName]>} [handler] - Optional callback function to handle the event.
 *
 * @returns {Trigger<EventPayloadMap<T>[EventName]>} A function to trigger the global event.
 *
 * @example
 * // Listening for an event
 * useGlobalEvent('shell:logout', handleLogout);
 *
 * @example
 * // Triggering an event
 * const triggerLogout = useGlobalEvent('shell:logout');
 * triggerLogout({ reason: 'user_initiated' });
 *
 * @remarks
 * - This hook automatically handles the cleanup of event listeners when the component unmounts.
 * - Use this for feature-to-feature communication in a decoupled architecture.
 * - Avoid using this for communication within the same feature.
 * - Use payloads sparingly to reduce coupling between features.
 */
export const useGlobalEvent = <EventName extends GlobalEvent, T>(
    eventName: EventName,
    handler?: Handler<EventPayloadMap<T>[EventName]>,
): Trigger<EventPayloadMap<T>[EventName]> => {
    useEventListener(eventName, handler);

    return (...data: OptionalArgTuple<EventPayloadMap<T>[EventName]>): void =>
        triggerGlobalEvent(eventName, ...data);
};

/**
 * Triggers a global event outside of React components.
 *
 * This function allows triggering global events from non-React contexts,
 * such as utility functions or services.
 *
 * @template EventName - The name of the global event (must be a key of EventPayloadMap).
 * @template T - The type of the event payload.
 *
 * @param {EventName} eventName - The name of the global event to trigger.
 * @param {...OptionalArgTuple<EventPayloadMap<T>[EventName]>} data - The payload data for the event.
 *
 * @example
 * triggerGlobalEvent('chat:initResume');
 *
 * @example
 * triggerGlobalEvent('shell:logout', { reason: 'session_expired' });
 *
 * @remarks
 * - Use this function when you need to trigger global events outside of React components.
 * - Ensure that the event name and payload match the types defined in EventPayloadMap.
 */
export function triggerGlobalEvent<EventName extends GlobalEvent, T>(
    eventName: EventName,
    ...data: OptionalArgTuple<EventPayloadMap<T>[EventName]>
): void {
    const event = new CustomEvent(eventName, { detail: data[0] });
    window.dispatchEvent(event);
}

export function addGlobalEventListener<EventName extends GlobalEvent>(
    eventName: EventName,
    eventHandler: GlobalEventListener<EventPayloadMap[EventName]>,
): void {
    window.addEventListener(eventName, eventHandler as EventListener);
}

export function removeGlobalEventListener<EventName extends GlobalEvent>(
    eventName: EventName,
    eventHandler: GlobalEventListener<EventPayloadMap[EventName]>,
): void {
    window.removeEventListener(eventName, eventHandler as EventListener);
}

function useEventListener<Payload>(eventName: GlobalEvent, handler?: Handler<Payload>): void {
    const savedHandler = useRef<Handler<Payload> | undefined>(undefined);

    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);

    useEffect(() => {
        if (!handler) {
            return;
        }
        const eventListener = ((event: CustomEvent) =>
            savedHandler.current?.(event.detail)) as EventListener;
        window.addEventListener(eventName, eventListener);
        // eslint-disable-next-line consistent-return
        return () => {
            window.removeEventListener(eventName, eventListener);
        };
    }, [eventName]);
}
